import React, { useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import FinanceEntryFields, { isFinanceEntryValid } from "@/components/FinanceEntryFields";
import { useAuth } from "@/context/AuthContext";
import { Link as LinkIcon, Eye } from "lucide-react";
import DriveLinkPreviewModal from "@/components/DriveLinkPreviewModal";

const TODAY = () => new Date().toISOString().slice(0, 10);
const MONTH = () => new Date().toISOString().slice(0, 7);

const emptyForm = (type) => ({
  item: "", quantity: 1, estimated_cost: "", justification: "",
  project_id: "", department: "", category: "", sub_category: "", cost_type: "", aop: "",
  vendor_id: "", vendor: "", payment_mode: "", source_id: "", drive_link: "",
  month: MONTH(), date: TODAY(),
  attachment_url: "", attachment_name: "", attachment_kind: "",
  request_type: type, receipt_url: "",
});

/**
 * Feb 2026 — Home Command Center's "Raise Purchase Request" / "New
 * Reimbursement" buttons. Standalone (own form state, no Purchases.jsx
 * page context) so they open as a direct modal on the Dashboard — the
 * backend (`require_internal`) already lets every non-client/vendor
 * staff member file one, so this must never depend on Finance page access.
 */
export default function NewPurchaseRequestDialog({ open, onOpenChange, defaultType = "purchase", onSaved }) {
  const { user } = useAuth();
  const [form, setForm] = useState(() => emptyForm(defaultType));
  const [docPreview, setDocPreview] = useState(null);
  const isReimbursement = form.request_type === "reimbursement";

  const resetForm = () => setForm(emptyForm(defaultType));

  const patchForm = (patch) => setForm((f) => ({ ...f, ...patch }));
  const onLinkChange = (v) => setForm((f) => ({ ...f, attachment_url: v, attachment_name: v ? "Google Drive link" : "", attachment_kind: v ? "link" : "" }));

  const validatorView = useMemo(() => ({ ...form, amount: Number(form.estimated_cost) }), [form]);
  const isFormValid = useMemo(() => {
    if (!form.item?.trim()) return false;
    if (Number(form.quantity) <= 0) return false;
    if (!form.justification?.trim()) return false;
    if (isReimbursement && !form.receipt_url?.trim()) return false;
    return isFinanceEntryValid(validatorView, { role: user?.role });
  }, [form, validatorView, isReimbursement, user?.role]);

  const submit = async () => {
    if (!isFormValid) return;
    try {
      await api.post("/purchase-requests", {
        ...form,
        quantity: Number(form.quantity),
        estimated_cost: Number(form.estimated_cost),
      });
      toast.success(isReimbursement ? "Reimbursement submitted" : "Request submitted");
      onOpenChange(false);
      resetForm();
      onSaved?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) resetForm(); }}>
      <DialogContent className="rounded-sm max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="npr-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">{isReimbursement ? "New reimbursement" : "New purchase request"}</DialogTitle>
          <DialogDescription>{isReimbursement ? "Claim back money you've already spent out-of-pocket — attach the receipt below." : "Submitted for Finance/Founder approval before anything is ordered."}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="md:col-span-2">
              <Label className="label-overline">Item *</Label>
              <Input className="rounded-sm mt-1" data-testid="npr-item" value={form.item} onChange={(e) => setForm({ ...form, item: e.target.value })} />
            </div>
            <div>
              <Label className="label-overline">Qty *</Label>
              <Input type="number" min="1" className="rounded-sm mt-1" data-testid="npr-qty" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="label-overline">{isReimbursement ? "Amount spent ₹ *" : "Estimated cost ₹ *"}</Label>
              <Input type="number" min="0" className="rounded-sm mt-1" data-testid="npr-cost" value={form.estimated_cost} onChange={(e) => setForm({ ...form, estimated_cost: e.target.value })} />
            </div>
            <div>
              <Label className="label-overline">Date *</Label>
              <Input type="date" className="rounded-sm mt-1" data-testid="npr-date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
            </div>
          </div>

          <div className="pt-3 border-t border-slate-200">
            <FinanceEntryFields value={form} onChange={patchForm} testidPrefix="npr" />
          </div>

          <div>
            <Label className="label-overline">Justification *</Label>
            <Textarea rows={2} className="rounded-sm mt-1" data-testid="npr-justification" value={form.justification} onChange={(e) => setForm({ ...form, justification: e.target.value })} />
          </div>

          {isReimbursement ? (
            <div className="border border-emerald-200 rounded-sm p-3 bg-emerald-50/40">
              <Label className="label-overline flex items-center gap-1"><LinkIcon size={11} /> Google Drive receipt link <span className="text-red-500">*</span></Label>
              <div className="flex items-center gap-1.5 mt-1.5">
                <Input className="rounded-sm" placeholder="https://drive.google.com/file/d/…" value={form.receipt_url} onChange={(e) => setForm({ ...form, receipt_url: e.target.value })} data-testid="npr-receipt-input" />
                {form.receipt_url && (
                  <button type="button" onClick={() => setDocPreview({ url: form.receipt_url, title: "Reimbursement receipt" })} className="shrink-0 text-slate-400 hover:text-[#F54800]" data-testid="npr-receipt-preview"><Eye size={15} /></button>
                )}
              </div>
              <div className="text-[10px] text-slate-500 mt-1">Required — paste a link to the receipt/invoice proving this was already paid for out-of-pocket.</div>
            </div>
          ) : (
            <div className="border border-slate-200 rounded-sm p-3 bg-slate-50/60">
              <Label className="label-overline">Invoice / PI — Google Drive link (optional)</Label>
              <div className="flex items-center gap-1.5 mt-1.5">
                <Input className="rounded-sm" placeholder="https://drive.google.com/file/d/…" value={form.attachment_url} onChange={(e) => onLinkChange(e.target.value)} data-testid="npr-link-input" />
                {form.attachment_url && (
                  <button type="button" onClick={() => setDocPreview({ url: form.attachment_url, title: "Purchase request attachment" })} className="shrink-0 text-slate-400 hover:text-[#F54800]" data-testid="npr-drive-link-preview"><Eye size={15} /></button>
                )}
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={!isFormValid} data-testid="npr-submit"
            className="rounded-sm bg-[#F54800] hover:bg-[#D63E00] text-white disabled:bg-slate-200 disabled:text-slate-400 disabled:cursor-not-allowed">
            Submit
          </Button>
        </DialogFooter>
      </DialogContent>
      <DriveLinkPreviewModal open={!!docPreview} url={docPreview?.url} title={docPreview?.title} onClose={() => setDocPreview(null)} />
    </Dialog>
  );
}
